import { Injectable } from '@angular/core';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IBaseDeliverableAnalyticsApi, ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { TStatusBadge } from '@shared/components/status-badge/status-badge.interface';
import { DATE_FORMAT, DATE_TIME_FORMAT, DateUtilitiesService } from '@utilities/date-utilities.service';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import {
  ICampaignDetailsObservationsFilterOption,
  ICampaignDetailsObservationsFilterState,
  ICampaignDetailsObservationsGroupedChildRow,
  ICampaignDetailsObservationsGroupedParentRow,
  ICampaignDetailsObservationsStatusOption,
  ICampaignDetailsObservationsTableRow,
  TCampaignDetailsObservationsGroupedRow,
  TCampaignDetailsObservationsRowStatusKey,
} from './campaign-details-observations.interface';

interface IKeywordEntry {
  findingsCount: number;
}

interface IKeywordHoldable {
  keywords?: IKeywordEntry[];
}

@Injectable()
export class CampaignDetailsObservationsService {
  private readonly statusOrder: TCampaignDetailsObservationsRowStatusKey[] = ['failed', 'problem', 'processing', 'ok'];

  private readonly statusBadgeOrder: EAnalyticsStatus[] = [
    EAnalyticsStatus.failed,
    EAnalyticsStatus.processing,
    EAnalyticsStatus.ok,
  ];

  constructor(
    private readonly dateUtils: DateUtilitiesService,
    private readonly deliverableUtilities: DeliverableUtilitiesService,
  ) {}

  public buildFilterOptions(
    rows: ICampaignDetailsObservationsTableRow[],
    translate: TranslateService,
  ): {
    observationTypes: ICampaignDetailsObservationsFilterOption<EDeliverableType>[];
  } {
    const observationTypes = Array.from(new Set(rows.map((row) => row.deliverableType)))
      .map((type) => ({
        label: translate.instant(`shared.deliverableTypes.${type}`) as string,
        value: type,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));

    return { observationTypes };
  }

  public buildRows(analyticsNodes: IAnalyticsNode[] | null): ICampaignDetailsObservationsTableRow[] {
    if (!analyticsNodes?.length) {
      return [];
    }

    const rows: ICampaignDetailsObservationsTableRow[] = [];

    for (const analyticsNode of analyticsNodes) {
      const sourceCta = this.getBestMatchingCta(analyticsNode);

      if (!sourceCta) {
        continue;
      }

      for (const config of this.deliverableUtilities.getDeliverableCollectionConfigs(sourceCta.ctaAnalytics)) {
        if (!this.deliverableUtilities.isTrackingDeliverable(config.type)) {
          continue;
        }

        const analyticsEntries = (sourceCta.ctaAnalytics[config.collectionKey] ?? []) as (IBaseDeliverableAnalyticsApi &
          IKeywordHoldable)[];

        for (const analyticsEntry of analyticsEntries) {
          const resolvedStatus = analyticsEntry.statusManual ?? analyticsEntry.status ?? EAnalyticsStatus.processing;

          if (!this.hasFindings(analyticsEntry) && resolvedStatus === EAnalyticsStatus.ok) {
            continue;
          }

          rows.push(this.buildTableRow(analyticsNode, sourceCta, analyticsEntry, config.type));
        }
      }
    }

    return rows;
  }

  public buildStatusOptions(
    rows: ICampaignDetailsObservationsTableRow[],
    selectedStatuses: Set<EAnalyticsStatus>,
  ): ICampaignDetailsObservationsStatusOption[] {
    return this.statusBadgeOrder.map((status, index): ICampaignDetailsObservationsStatusOption => {
      const count = rows.filter((row) => row.statusFilterValue === status).length;

      let badgeStatus: TStatusBadge;

      if (status === EAnalyticsStatus.ok) {
        badgeStatus = 'findings';
      } else if (status === EAnalyticsStatus.failed) {
        badgeStatus = 'noFindings';
      } else {
        badgeStatus = status;
      }

      return {
        colorScheme: 'tracking',
        count,
        isDisabled: count === 0 && !selectedStatuses.has(status),
        key: status,
        sortOrder: index,
        status: badgeStatus,
      };
    });
  }

  public buildGroupedRows(rows: ICampaignDetailsObservationsTableRow[]): TCampaignDetailsObservationsGroupedRow[] {
    const groupedRowMap = new Map<string, TCampaignDetailsObservationsGroupedRow>();

    for (const row of rows) {
      const groupId = this.buildGroupKey(row);
      const existingGroup = groupedRowMap.get(groupId);

      if (!existingGroup) {
        const parent: ICampaignDetailsObservationsGroupedParentRow = {
          analyticsNodeId: row.analyticsNodeId,
          creatorIcon: row.creatorIcon,
          creatorId: row.creatorId,
          creatorName: row.creatorName,
          ctaTemplateId: row.ctaTemplateId,
          dateLabel: row.dateLabel,
          dateSortValue: row.dateSortValue,
          id: groupId,
          observationsCount: 0,
          platformFeature: row.platformFeature,
        };

        groupedRowMap.set(groupId, {
          children: [],
          id: groupId,
          isExpanded: true,
          parent,
        });
      }

      const groupedRow = groupedRowMap.get(groupId);

      if (!groupedRow) {
        continue;
      }

      const childRow: ICampaignDetailsObservationsGroupedChildRow = {
        id: row.id,
        rowStatus: row.rowStatus,
        rowStatusColorScheme: row.rowStatusColorScheme,
        sourceRow: row,
      };

      groupedRow.children.push(childRow);
      groupedRow.parent.observationsCount = groupedRow.children.length;
    }

    return Array.from(groupedRowMap.values());
  }

  public createInitialFilterState(): ICampaignDetailsObservationsFilterState {
    return {
      observationStatuses: new Set<EAnalyticsStatus>(),
      observationTypes: new Set<EDeliverableType>(),
    };
  }

  public filterRows(
    rows: ICampaignDetailsObservationsTableRow[],
    filterState: ICampaignDetailsObservationsFilterState,
  ): ICampaignDetailsObservationsTableRow[] {
    return rows.filter((row) => {
      if (filterState.observationTypes.size > 0 && !filterState.observationTypes.has(row.deliverableType)) {
        return false;
      }

      if (filterState.observationStatuses.size > 0 && !filterState.observationStatuses.has(row.statusFilterValue)) {
        return false;
      }

      return true;
    });
  }

  private getBestMatchingCta(analyticsNode: IAnalyticsNode): ICtaResultApi | null {
    if (!analyticsNode.content) {
      return null;
    }

    const ctas = Array.from(analyticsNode.content.ctas.values());

    if (!ctas.length) {
      return null;
    }

    return ctas.reduce((best, cta) => (cta.ctaAnalytics.matchingScore > best.ctaAnalytics.matchingScore ? cta : best));
  }

  private hasFindings(entry: IKeywordHoldable): boolean {
    return (entry.keywords ?? []).some((k) => k.findingsCount > 0);
  }

  private buildTableRow(
    analyticsNode: IAnalyticsNode,
    sourceCta: ICtaResultApi,
    analyticsEntry: IBaseDeliverableAnalyticsApi,
    deliverableType: EDeliverableType,
  ): ICampaignDetailsObservationsTableRow {
    const resolvedStatus = analyticsEntry.statusManual ?? analyticsEntry.status ?? EAnalyticsStatus.processing;
    const statusKey = this.resolveStatusKey(resolvedStatus);

    const executedAt = analyticsNode.content?.socialContent?.executionTime ?? analyticsNode.executionTime ?? null;
    const executedDate = executedAt ? new Date(executedAt) : null;
    const isDateOnly = analyticsNode.isDateOnlyExecution === true;
    const dateLabel = executedDate
      ? this.dateUtils.formatDate(executedDate, isDateOnly ? DATE_FORMAT : DATE_TIME_FORMAT)
      : '';

    return {
      analyticsNodeId: analyticsNode.id,
      creatorIcon: analyticsNode.creator.icon,
      creatorId: analyticsNode.creatorId,
      creatorName: analyticsNode.creator.name,
      ctaTemplateId: sourceCta.ctaId,
      dateLabel,
      dateSortValue: executedDate?.getTime() ?? 0,
      deliverableId: analyticsEntry.deliverableId,
      deliverableName: analyticsEntry.name,
      deliverableType,
      id: `${analyticsNode.id}_${sourceCta.ctaId}_${analyticsEntry.deliverableId}`,
      normalizedStatusKey: statusKey,
      platformFeature: analyticsNode.platformFeature,
      rowStatus:
        resolvedStatus === EAnalyticsStatus.ok
          ? 'findings'
          : resolvedStatus === EAnalyticsStatus.failed
            ? 'noFindings'
            : resolvedStatus,
      rowStatusColorScheme: 'tracking',
      statusFilterValue: resolvedStatus,
      statusSortValue: this.statusOrder.indexOf(statusKey),
    };
  }

  private resolveStatusKey(status: EAnalyticsStatus): TCampaignDetailsObservationsRowStatusKey {
    switch (status) {
      case EAnalyticsStatus.failed:
        return 'failed';
      case EAnalyticsStatus.ok:
        return 'ok';
      case EAnalyticsStatus.problem:
        return 'problem';
      default:
        return 'processing';
    }
  }

  private buildGroupKey(row: ICampaignDetailsObservationsTableRow): string {
    return `${row.creatorId}::${row.analyticsNodeId}`;
  }
}
